/**
 * useJarvisLiveEvents
 *
 * Polled während aktiver Voice-Session den Backend-Event-Stream
 * (`GET /api/heykiki/jarvis/events`) und pusht neue Mitteilungen via
 * `conversation.sendContextualUpdate(text)` an die laufende ElevenLabs-
 * Conversation. So kann Kiki proaktiv reagieren: „Neue Anfrage von
 * Müller", „Eingehender Anruf von Schmidt" etc.
 *
 * **Pattern**:
 *   - Nur aktiv wenn `isConnected === true` (sonst keine Voice-Session →
 *     sinnloser Traffic)
 *   - Polling-Intervall 8s — guter Trade-off zwischen Latenz und Traffic
 *   - Cursor-basiert (`latestTs`): jedes Event nur EINMAL pushen, kein
 *     Dedup-Hassle wenn das Backend mehrfach denselben Eintrag liefert
 *   - Prefix „JARVIS-EVENT:" damit der System-Prompt das als „external
 *     event" erkennt (siehe Prompt-Sektion „Live-Mitteilungen")
 *
 * **Wichtig**: `conversation`-Objekt KEINESFALLS als useEffect-Dependency
 * nutzen — der useConversation-Hook gibt bei jedem Render ein neues
 * Objekt zurück, was den useEffect re-trigger und in einer Schleife
 * mehrere parallele Poll-Ketten startet (sah sich in der ersten Version
 * als 1-3s Polling-Intervall statt 8s). Stattdessen via Ref weiterreichen.
 *
 * **Tradeoff zu Push (SSE/WS)**: Polling ist ~8s langsamer in der Latenz,
 * dafür triviale Integration ohne neue Infrastruktur. Bei Performance-
 * Problemen später auf SSE umstellen — Endpoint-Signatur bleibt gleich.
 */

import { useEffect, useRef } from 'react';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
const POLL_INTERVAL_MS = 8000;

/**
 * @param {Object} opts
 * @param {boolean} opts.isConnected — true wenn Voice-Session aktiv
 * @param {Object} opts.conversation — useConversation()-Rückgabe (für sendContextualUpdate)
 */
export default function useJarvisLiveEvents({ isConnected, conversation }) {
  // Ref auf die jeweils aktuelle conversation, damit der Effect sie
  // benutzen kann ohne von ihr abhängig zu sein. Update bei jedem Render.
  const conversationRef = useRef(conversation);
  useEffect(() => {
    conversationRef.current = conversation;
  });

  // Cursor — neuestes Event-ts, das wir schon gesehen haben. Wird beim
  // Session-Start auf jetzt gesetzt damit alte Events vor Aktivierung
  // NICHT nachträglich abgefeuert werden.
  const sinceRef = useRef(Date.now());

  useEffect(() => {
    // Nur eine Schleife wenn die Voice-Session aktiv ist.
    if (!isConnected) {
      sinceRef.current = Date.now(); // reset für nächste Session
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) return;

    let cancelled = false;
    let timeoutId = null;

    const poll = async () => {
      if (cancelled) return;
      try {
        const r = await axios.get(`${API_URL}/heykiki/jarvis/events`, {
          params: { since: sinceRef.current },
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const events = r.data?.events || [];
        const latestTs = r.data?.latestTs;

        // Cursor IMMER vorrücken (auch bei 0 Events) damit wir nicht jedes
        // Mal die letzten 30s scannen.
        if (typeof latestTs === 'number' && latestTs > sinceRef.current) {
          sinceRef.current = latestTs;
        }

        // Events an Kiki pushen — älteste zuerst damit die Reihenfolge stimmt
        const ordered = [...events].reverse();
        const conv = conversationRef.current;
        for (const ev of ordered) {
          if (!ev?.message) continue;
          try {
            // Prefix „JARVIS-EVENT:" instruiert den Agent das als externen
            // Hinweis zu behandeln (siehe System-Prompt-Sektion).
            conv?.sendContextualUpdate?.(`JARVIS-EVENT: ${ev.message}`);
            console.log('[Jarvis Live-Events] pushed:', ev.message);
          } catch (err) {
            console.warn('[Jarvis Live-Events] sendContextualUpdate failed:', err?.message);
          }
        }
      } catch {
        // Stille — Backend hat 5xx, Token expired, was auch immer. Nächster
        // Poll versucht's wieder. Kein Toast (sonst nervt es den User).
      } finally {
        if (!cancelled) {
          timeoutId = setTimeout(poll, POLL_INTERVAL_MS);
        }
      }
    };

    // Erster Poll nach 1s — gibt der Session Zeit sich zu etablieren,
    // bevor wir Events einwerfen.
    timeoutId = setTimeout(poll, 1000);

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isConnected]); // NUR isConnected als Dep — conversation läuft über Ref
}
